import { memo, useState, useCallback } from 'react';
import { CaseReportPropertyList } from '@/components/casereport/CaseReportPropertyList';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

function emptyPropertyForm() {
  return {
    tempid: null as any, item: '', estimatedValue: null as any,
    ownerId: '', npsdPersonOwner: null as any, npsdBusinessOwner: null as any, npsdClientOwner: null as any,
    reportedAs: null as any, location: '', damages: '', brand: '', model: '',
    year: '', colors: '', serialNum: '', storageLoc: '', officerNotes: '',
  };
}

interface Props {
  propertyList: any[];
  setPropertyList: (p: any[]) => void;
  propertyOwnerOptions: { id: string; name: string }[];
  reportedAsOptions: any[];
  isReadOnly: boolean;
  officerLabel: string;
}

export const CaseReportPropertySection = memo(function CaseReportPropertySection({
  propertyList, setPropertyList, propertyOwnerOptions, reportedAsOptions, isReadOnly, officerLabel,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyPropertyForm);
  const [valueDollars, setValueDollars] = useState('');

  const openModal = useCallback((item?: any) => {
    if (item) {
      let ownerId = '';
      if (item.npsdPersonOwner) ownerId = item.npsdPersonOwner + '_P';
      else if (item.npsdBusinessOwner) ownerId = item.npsdBusinessOwner + '_B';
      else if (item.npsdClientOwner) ownerId = item.npsdClientOwner + '_C';
      setForm({
        tempid: item.tempid, item: item.item || '',
        estimatedValue: item.estimatedValue ? { ...item.estimatedValue } : null,
        ownerId, npsdPersonOwner: item.npsdPersonOwner, npsdBusinessOwner: item.npsdBusinessOwner, npsdClientOwner: item.npsdClientOwner,
        reportedAs: item.reportedAs, location: item.location || '', damages: item.damages || '',
        brand: item.brand || '', model: item.model || '', year: item.year || '', colors: item.colors || '',
        serialNum: item.serialNum || '', storageLoc: item.storageLoc || '', officerNotes: item.officerNotes || '',
      });
      setValueDollars(item.estimatedValue?.value != null ? (item.estimatedValue.value / 100).toFixed(2) : '');
    } else {
      setForm(emptyPropertyForm());
      setValueDollars('');
    }
    setShowModal(true);
  }, []);

  const saveProperty = useCallback(() => {
    if (!form.item) { App.showPopupNotification('Item is required.', 'error'); return; }

    let npsdPersonOwner: any = null, npsdBusinessOwner: any = null, npsdClientOwner: any = null;
    if (form.ownerId) {
      const t = form.ownerId.slice(-1);
      if (t === 'P') npsdPersonOwner = parseInt(form.ownerId.replace('_P', ''));
      else if (t === 'B') npsdBusinessOwner = parseInt(form.ownerId.replace('_B', ''));
      else if (t === 'C') npsdClientOwner = parseInt(form.ownerId.replace('_C', ''));
    }

    let estimatedValue: any = null;
    if (valueDollars !== '') {
      const cents = Math.round(parseFloat(valueDollars) * 100);
      if (!isNaN(cents)) estimatedValue = { code: 'USD', value: cents };
    }

    const entry = {
      tempid: form.tempid || getInsecureUUID(), item: form.item, estimatedValue,
      npsdPersonOwner, npsdBusinessOwner, npsdClientOwner, reportedAs: form.reportedAs,
      location: form.location, damages: form.damages, brand: form.brand, model: form.model,
      year: form.year, colors: form.colors, serialNum: form.serialNum,
      storageLoc: form.storageLoc, officerNotes: form.officerNotes,
    };

    if (form.tempid) {
      const idx = CaseRpt.currentMDTRpt.propertyList.findIndex((p: any) => p.tempid === form.tempid);
      if (idx > -1) CaseRpt.currentMDTRpt.propertyList[idx] = entry;
    } else {
      CaseRpt.currentMDTRpt.propertyList.push(entry);
    }
    setPropertyList(CaseRpt.currentMDTRpt.propertyList.slice());
    setShowModal(false);
    setForm(emptyPropertyForm());
  }, [form, valueDollars, setPropertyList]);

  return (
    <>
      <div className="tab-pane case-report-details-tab" role="tabpanel" id="Property">
        <CaseReportPropertyList
          items={propertyList}
          isReadOnly={isReadOnly}
          ownerOptions={propertyOwnerOptions}
          reportedAsOptions={reportedAsOptions}
          onChange={(p: any[]) => { CaseRpt.currentMDTRpt.propertyList = p; setPropertyList(p); }}
          onAdd={() => openModal()}
          onEdit={(item: any) => openModal(item)}
        />
      </div>

      <ConfirmModal
        open={showModal}
        title={form.tempid ? 'Edit Property' : 'Add Property'}
        confirmText="Save"
        confirmClass="primary"
        className="add-property-modal"
        onConfirm={saveProperty}
        onCancel={() => { setShowModal(false); setForm(emptyPropertyForm()); setValueDollars(''); }}
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Item</label>
            <input type="text" value={form.item}
              onChange={e => setForm(f => ({ ...f, item: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Estimated Value</label>
            <div className="money-input-wrapper">
              <span className="money-prefix">$</span>
              <input type="number" step="0.01" min="0" value={valueDollars}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setValueDollars(v);
                }} />
            </div>
          </div>
          <div className="form-group">
            <label>Owner</label>
            <Dropdownlist value={form.ownerId} items={propertyOwnerOptions}
              labelKey="name" valueKey="id" clearable={true} placeholder="Select Owner..."
              onChange={(val) => setForm(f => ({ ...f, ownerId: val as string }))} />
          </div>
          <div className="form-group">
            <label>Reported As</label>
            <Dropdownlist value={form.reportedAs} items={reportedAsOptions}
              labelKey="value" valueKey="code" clearable={true} placeholder="Select..."
              onChange={(val) => setForm(f => ({ ...f, reportedAs: val }))} />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input type="text" value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Damages</label>
            <input type="text" value={form.damages}
              onChange={e => setForm(f => ({ ...f, damages: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Brand</label>
            <input type="text" value={form.brand}
              onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Model</label>
            <input type="text" value={form.model}
              onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Year</label>
            <input type="text" value={form.year}
              onChange={e => setForm(f => ({ ...f, year: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Color</label>
            <input type="text" value={form.colors}
              onChange={e => setForm(f => ({ ...f, colors: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Serial Number</label>
            <input type="text" value={form.serialNum}
              onChange={e => setForm(f => ({ ...f, serialNum: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Storage Location</label>
            <input type="text" value={form.storageLoc}
              onChange={e => setForm(f => ({ ...f, storageLoc: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>Notes</label>
            <textarea value={form.officerNotes}
              onChange={e => setForm(f => ({ ...f, officerNotes: e.target.value }))} rows={3} />
          </div>
        </div>
      </ConfirmModal>
    </>
  );
});
